import { BodyContentType } from "@/domain/entities/emails/Email";
import sanitizeHTML from "@/lib/sanitizeHTML";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const IFRAME_RESIZE_MESSAGE = "mailvista:iframe-resize";
const IFRAME_HSCROLL_TO_MESSAGE = "mailvista:iframe-hscroll-to";
const IFRAME_HSCROLL_POS_MESSAGE = "mailvista:iframe-hscroll-pos";

const FONT_FALLBACK_STACK = "'Source Sans 3', Arial, Helvetica, sans-serif";

const GENERIC_FONT_FAMILIES = new Set([
    "serif",
    "sans-serif",
    "monospace",
    "cursive",
    "fantasy",
    "system-ui",
    "ui-serif",
    "ui-sans-serif",
    "ui-monospace",
    "ui-rounded",
    "math",
    "emoji",
]);

// Idempotent: a value already ending in a generic family (deliberate choice or
// previous pass) is left untouched, so re-running never stacks fallbacks.
function withFontFallback(value: string): string {
    const trimmed = value.trim();
    if (!trimmed) return value;

    const lastFamily = trimmed
        .split(",")
        .pop()!
        .trim()
        .replace(/["']/g, "")
        .toLowerCase();
    if (GENERIC_FONT_FAMILIES.has(lastFamily)) return trimmed;

    return `${trimmed}, ${FONT_FALLBACK_STACK}`;
}

// Custom email fonts can't load in the sandboxed iframe and would drop to the
// browser default serif (Times New Roman); give every declared font a web-safe fallback.
function applyFontFallback(doc: Document): void {
    for (const element of doc.querySelectorAll<HTMLElement>(
        "[style], font[face]",
    )) {
        const current =
            element.style.fontFamily || (element.getAttribute("face") ?? "");
        if (current) element.style.fontFamily = withFontFallback(current);
    }

    for (const styleElement of doc.querySelectorAll("style")) {
        const css = styleElement.textContent;
        if (!css) continue;

        // Match @font-face blocks first so their font-family descriptor (the font's name) is skipped.
        styleElement.textContent = css.replace(
            /@font-face\s*\{[^}]*\}|font-family\s*:\s*([^;}]+)/gi,
            (match, value?: string) =>
                value === undefined
                    ? match
                    : `font-family: ${withFontFallback(value)}`,
        );
    }
}

// CSS Values 4: 1in = 96px = 72pt.
const CSS_PX: Record<string, number> = { px: 1, pt: 96 / 72, in: 96 };

const MAX_KEPT_WIDTH = 300;

function toPx(value: string): number {
    const match = /^([\d.]+)(px|pt|in)$/.exec(value);
    return match ? Number(match[1]) * CSS_PX[match[2]] : 0;
}

function declaredWidth(element: HTMLElement): number {
    if (element.style.width) return toPx(element.style.width);
    const attribute = (element.getAttribute("width") ?? "").trim();
    const match = /^([\d.]+)(px)?$/i.exec(attribute);
    return match ? Number(match[1]) : 0;
}

// A % max-width lets auto-layout tables crush images (AB#1928): keep the declared
// width of small table images when the row has room for it.
function markKeptWidthImages(doc: Document): void {
    type Cell = { widest: number; images: HTMLImageElement[] };
    const rows = new Map<Element | null, Map<Element, Cell>>();

    for (const img of doc.querySelectorAll<HTMLImageElement>(
        "td img, th img",
    )) {
        const width = declaredWidth(img);
        if (!width || width > MAX_KEPT_WIDTH) continue;

        const cell = img.closest<HTMLElement>("td, th")!;
        const cellWidth = declaredWidth(cell);
        if (cellWidth && cellWidth < width - 1) continue;
        if (cell.closest("table")?.style.tableLayout === "fixed") continue;

        const row = cell.closest("tr");
        const cells = rows.get(row) ?? new Map<Element, Cell>();
        rows.set(row, cells);
        const entry = cells.get(cell) ?? { widest: 0, images: [] };
        cells.set(cell, entry);
        entry.widest = Math.max(entry.widest, width);
        entry.images.push(img);
    }

    for (const cells of rows.values()) {
        let total = 0;
        for (const { widest } of cells.values()) total += widest;
        if (total > MAX_KEPT_WIDTH) continue;
        for (const { images } of cells.values()) {
            for (const img of images) img.setAttribute("data-keep-width", "");
        }
    }
}

function renderIframeStyleElement(): HTMLStyleElement {
    const element = document.createElement("style");
    element.textContent = `
        /* html is the viewport scroll container: window.scrollTo() and window.scrollX target it.
           Setting overflow-x on body instead would create a separate body scroll container that
           window.scrollTo() cannot reach, breaking the sticky scrollbar commands. */
        html {
            overflow-y: hidden !important;
            overflow-x: auto;
            scrollbar-width: none;
            margin: 0;
            padding: 0;
        }

        html::-webkit-scrollbar {
            display: none;
        }

        body {
            overflow: visible;
            margin: 0;
            padding: 0;
            height: fit-content;
            display: flow-root;
        }

        body, body * {
            font-family: ${FONT_FALLBACK_STACK};
        }

        /* No h1/h2/h3/blockquote rules here on purpose: this document gets no
           CSS reset, so those tags keep the plain browser default look. The
           compose-side editor (src/components/mailEditor/RichTextEditor.tsx,
           EDITOR_CONTENT_CLASSES) re-creates that same look on top of
           Tailwind's preflight reset — keep both in sync if either changes. */

        img {
            max-width: 100%;
            height: auto;
        }

        img[data-keep-width] {
            max-width: 100vw;
        }

        table[style*="height:100%"] {
            height: auto !important;
        }

        .plain-text-content {
            white-space: pre-wrap;
            word-break: break-word;
            margin: 0;
            font-family: inherit;
        }
    `;

    return element;
}

function renderIframeScriptElement(): HTMLScriptElement {
    const element = document.createElement("script");
    element.textContent = `
        (function () {
            const RESIZE_MSG = "${IFRAME_RESIZE_MESSAGE}";
            const HSCROLL_TO_MSG = "${IFRAME_HSCROLL_TO_MESSAGE}";
            const HSCROLL_POS_MSG = "${IFRAME_HSCROLL_POS_MESSAGE}";

            let last = { height: -1, scrollWidth: -1 };
            // Prevents the echo loop: parent → scroll → scroll event → parent → ...
            let skipNextScrollReport = false;

            const postSize = () => {
                const body = document.body;
                if (!body) return;
                const height = body.scrollHeight;
                const scrollWidth = body.scrollWidth;
                if (height === last.height && scrollWidth === last.scrollWidth) return;
                last = { height, scrollWidth };
                window.parent.postMessage({ type: RESIZE_MSG, height, scrollWidth }, '*');
            };

            window.addEventListener('scroll', () => {
                if (skipNextScrollReport) {
                    skipNextScrollReport = false;
                    return;
                }
                window.parent.postMessage({ type: HSCROLL_POS_MSG, scrollX: window.scrollX }, '*');
            }, { passive: true });

            window.addEventListener('message', (e) => {
                if (e.data?.type === HSCROLL_TO_MSG && typeof e.data.scrollX === 'number') {
                    skipNextScrollReport = true;
                    window.scrollTo({ left: e.data.scrollX, behavior: 'instant' });
                }
            });

            new ResizeObserver(postSize).observe(document.body);
            postSize();
        })();
    `;

    return element;
}

/**
 * Builds the sandboxed iframe document: sanitized content + injected style/script
 * that drive the auto-height and the sticky horizontal scrollbar.
 */
export function useIframeSrcDoc(
    content: string,
    contentType: BodyContentType,
): string {
    return useMemo(() => {
        const sanitized = sanitizeHTML(content);

        const parser = new DOMParser();
        const doc = parser.parseFromString(sanitized, "text/html");

        if (contentType === BodyContentType.TEXT) {
            const plainText = doc.body.textContent || "";
            doc.body.innerHTML = "";

            const pre = doc.createElement("pre");
            pre.className = "plain-text-content";
            pre.textContent = plainText;

            doc.body.appendChild(pre);
        }

        applyFontFallback(doc);
        try {
            markKeptWidthImages(doc);
        } catch {
            // Best-effort: unmarked images just keep max-width: 100%.
        }

        doc.head.appendChild(renderIframeStyleElement());
        doc.body.appendChild(renderIframeScriptElement());

        return doc.documentElement.outerHTML;
    }, [content, contentType]);
}

/**
 * Parent side of the postMessage contract with the sandboxed iframe: applies the
 * reported height, tracks content/container width and scroll position, and exposes
 * a clamped `scrollTo` command.
 */
export function useIframeBridge() {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [contentWidth, setContentWidth] = useState(0);
    const [containerWidth, setContainerWidth] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);

    useEffect(() => {
        const iframe = iframeRef.current;
        if (!iframe) return;

        const handleMessage = (event: MessageEvent) => {
            if (event.source !== iframe.contentWindow) return;
            const { data } = event;

            if (data?.type === IFRAME_RESIZE_MESSAGE) {
                if (typeof data.height === "number") {
                    iframe.style.height = `${data.height}px`;
                }
                if (typeof data.scrollWidth === "number") {
                    setContentWidth(data.scrollWidth);
                }
            } else if (
                data?.type === IFRAME_HSCROLL_POS_MESSAGE &&
                typeof data.scrollX === "number"
            ) {
                setScrollLeft(data.scrollX);
            }
        };

        window.addEventListener("message", handleMessage);

        return () => window.removeEventListener("message", handleMessage);
    }, []);

    // Container width is measured in the parent, independently of the iframe internals.
    useEffect(() => {
        const iframe = iframeRef.current;
        if (!iframe) return;

        const observer = new ResizeObserver(() =>
            setContainerWidth(iframe.clientWidth),
        );
        observer.observe(iframe);
        setContainerWidth(iframe.clientWidth);

        return () => observer.disconnect();
    }, []);

    const scrollTo = useCallback(
        (newScrollLeft: number) => {
            const max = Math.max(0, contentWidth - containerWidth);
            const clamped = Math.max(0, Math.min(newScrollLeft, max));
            setScrollLeft(clamped);
            iframeRef.current?.contentWindow?.postMessage(
                { type: IFRAME_HSCROLL_TO_MESSAGE, scrollX: clamped },
                "*",
            );
        },
        [contentWidth, containerWidth],
    );

    return { iframeRef, contentWidth, containerWidth, scrollLeft, scrollTo };
}
